// Captures the frame currently shown in the fullscreen viewer (video or 3D) as
// an image data URL, so it can be staged in the Magic Studio basket as a normal
// reference. Returns null when the frame can't be read — element missing, not
// yet decoded, or a tainted canvas (cross-origin media without CORS).

export type CaptureMode = 'video' | '3d';

// Cap the captured still so the resulting data URL stays small enough to live in
// the store and be sent inline to the model.
const MAX_CAPTURE_DIM = 1600;

function fitWithin(w: number, h: number, max: number): { w: number; h: number } {
  if (w <= max && h <= max) return { w, h };
  const scale = Math.min(max / w, max / h);
  return { w: Math.round(w * scale), h: Math.round(h * scale) };
}

function timecode(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}m${String(s % 60).padStart(2, '0')}s`;
}

function drawToDataUrl(
  source: CanvasImageSource,
  srcW: number,
  srcH: number,
  mime: string,
  quality?: number,
): string | null {
  const { w, h } = fitWithin(srcW, srcH, MAX_CAPTURE_DIM);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, w, h);
  return canvas.toDataURL(mime, quality);
}

export function captureCurrentFrame(
  mode: CaptureMode,
  baseName: string,
): { name: string; url: string } | null {
  try {
    if (mode === 'video') {
      const video = document.querySelector<HTMLVideoElement>('.ch-file-viewer__video');
      if (!video || !video.videoWidth || !video.videoHeight) return null;
      const url = drawToDataUrl(video, video.videoWidth, video.videoHeight, 'image/jpeg', 0.92);
      return url ? { name: `${baseName}_frame_${timecode(video.currentTime)}.jpg`, url } : null;
    }

    // 3D — the three.js renderer keeps its drawing buffer (preserveDrawingBuffer
    // in use3dHook), so its live canvas can be read directly.
    const gl = document.querySelector<HTMLCanvasElement>('.gen-media-3d__host canvas');
    if (!gl || !gl.width || !gl.height) return null;
    const url = drawToDataUrl(gl, gl.width, gl.height, 'image/png');
    return url ? { name: `${baseName}_view.png`, url } : null;
  } catch {
    // Tainted canvas / SecurityError.
    return null;
  }
}
